// Reply templates manager — the two-pane create/edit/delete surface.

import { Controller } from "stimulus";

interface Prefill {
  name?: string;
  category?: string;
  body?: string;
  fresh?: boolean;
}

export default class extends Controller {
  static targets = ["mount", "list", "name", "category", "body", "delete", "error"];

  static values = {
    managerUrl: String,
    createUrl: String
  };

  declare readonly mountTarget: HTMLElement;
  declare readonly hasListTarget: boolean;
  declare readonly listTarget: HTMLElement;
  declare readonly hasNameTarget: boolean;
  declare readonly nameTarget: HTMLInputElement;
  declare readonly categoryTarget: HTMLSelectElement;
  declare readonly bodyTarget: HTMLTextAreaElement;
  declare readonly hasDeleteTarget: boolean;
  declare readonly deleteTarget: HTMLButtonElement;
  declare readonly hasErrorTarget: boolean;
  declare readonly errorTarget: HTMLElement;

  declare readonly managerUrlValue: string;
  declare readonly createUrlValue: string;

  private editingId: string | null = null;
  private loading: Promise<void> | null = null;

  connect(): void {
    // Settings renders the manager server-side; the Inbox mounts empty and fetches on first open.
    if (this.mountTarget.children.length) this.selectFirst();
  }

  // ---- open / close (Inbox modal only) ----

  open(event?: CustomEvent<Prefill>): void {
    const prefill = event?.detail;

    this.ensureLoaded().then(() => {
      // `fresh` clears the selection BEFORE the prefill lands. Without it, "Save this reply" over an
      // already-selected template would overwrite that template with the draft.
      if (prefill?.fresh) this.loadInto(null);
      if (prefill) this.applyPrefill(prefill);

      this.element.classList.add("open");
      this.element.setAttribute("aria-hidden", "false");
      if (this.hasNameTarget) this.nameTarget.focus();
    });
  }

  close(): void {
    if (!this.isModal) return;

    this.element.classList.remove("open");
    this.element.setAttribute("aria-hidden", "true");
  }

  closeOnBackdrop(event: Event): void {
    if (event.target === this.element) this.close();
  }

  closeOnEscape(event: KeyboardEvent): void {
    if (event.key !== "Escape") return;

    this.close();
  }

  // ---- list ----

  newTemplate(): void {
    this.loadInto(null);
    if (this.hasNameTarget) this.nameTarget.focus();
  }

  select(event: Event): void {
    const row = (event.target as HTMLElement).closest(".tplm-row") as HTMLElement | null;
    if (!row) return;

    this.loadInto(row);
  }

  insertToken(event: Event): void {
    const token = (event.currentTarget as HTMLElement).dataset.token || "";
    const field = this.bodyTarget;
    const start = field.selectionStart ?? field.value.length;
    const end = field.selectionEnd ?? start;

    field.value = field.value.slice(0, start) + token + field.value.slice(end);
    // Caret after the token, not where it was: three chips in a row should give three tokens in a
    // row, which the mockup's version does not.
    field.selectionStart = field.selectionEnd = start + token.length;
    field.focus();
  }

  // ---- persistence ----

  save(): void {
    const name = this.nameTarget.value.trim();
    if (!name) {
      this.showError("Give the template a name.");
      this.nameTarget.focus();
      return;
    }

    const url = this.editingId ? `${this.createUrlValue}/${this.editingId}` : this.createUrlValue;

    this.request(url, this.editingId ? "PATCH" : "POST", {
      reply_template: {name, category: this.categoryTarget.value, body: this.bodyTarget.value}
    }).then((data) => {
      if (data.errors) return this.showError(data.errors.join(" "));

      const savedId = data.template.id;
      this.reload().then(() => this.selectById(savedId));
      this.announceChange();
    });
  }

  destroy(): void {
    if (!this.editingId) return;
    if (!window.confirm("Delete this template? This cannot be undone.")) return;

    this.request(`${this.createUrlValue}/${this.editingId}`, "DELETE").then((data) => {
      if (data.errors) return this.showError(data.errors.join(" "));

      this.editingId = null;
      this.reload().then(() => this.selectFirst());
      this.announceChange();
    });
  }

  // ---- internals ----

  private get isModal(): boolean {
    return this.element.classList.contains("tplm-backdrop");
  }

  private ensureLoaded(): Promise<void> {
    if (this.mountTarget.children.length) return Promise.resolve();
    if (this.loading) return this.loading;

    this.loading = this.reload().then(() => {
      this.loading = null;
      this.selectFirst();
    });

    return this.loading;
  }

  private reload(): Promise<void> {
    return fetch(this.managerUrlValue, {headers: {Accept: "text/html"}, credentials: "same-origin"})
      .then((response) => response.text())
      .then((html) => {
        this.mountTarget.innerHTML = html;
      });
  }

  private selectFirst(): void {
    const first = this.hasListTarget ? this.listTarget.querySelector(".tplm-row") : null;
    this.loadInto(first as HTMLElement | null);
  }

  private selectById(id: string): void {
    const row = this.hasListTarget
      ? this.listTarget.querySelector(`.tplm-row[data-template-id="${id}"]`)
      : null;

    this.loadInto(row as HTMLElement | null);
  }

  private loadInto(row: HTMLElement | null): void {
    if (!this.hasNameTarget) return;

    this.editingId = row?.dataset.templateId || null;
    this.nameTarget.value = row?.dataset.name || "";
    this.categoryTarget.value = row?.dataset.category || "custom";
    this.bodyTarget.value = row?.dataset.body || "";

    if (this.hasListTarget) {
      this.listTarget.querySelectorAll(".tplm-row.on").forEach((el) => el.classList.remove("on"));
      row?.classList.add("on");
    }
    if (this.hasDeleteTarget) this.deleteTarget.hidden = !this.editingId;
    this.clearError();
  }

  private applyPrefill(prefill: Prefill): void {
    if (!this.hasNameTarget) return;

    if (prefill.name !== undefined) this.nameTarget.value = prefill.name;
    if (prefill.category !== undefined) this.categoryTarget.value = prefill.category;
    if (prefill.body !== undefined) this.bodyTarget.value = prefill.body;
  }

  private request(url: string, method: string, body?: object): Promise<any> {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");

    return fetch(url, {
      method,
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": csrfToken || "",
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      body: body ? JSON.stringify(body) : undefined
    })
      .then((response) => response.json())
      .catch(() => ({errors: ["Something went wrong. Please try again."]}));
  }

  private announceChange(): void {
    window.dispatchEvent(new CustomEvent("reply-templates:changed"));
  }

  private showError(message: string): void {
    if (!this.hasErrorTarget) return;

    this.errorTarget.textContent = message;
    this.errorTarget.hidden = false;
  }

  private clearError(): void {
    if (!this.hasErrorTarget) return;

    this.errorTarget.textContent = "";
    this.errorTarget.hidden = true;
  }
}
